import {Edge, Node, ReactFlow, useReactFlow} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import FamilyTreeEdge from "./FamilyTreeEdge";
import FamilyTreeNode from "./FamilyTreeNode";
import ReactFlowZoomControls, {FAMILY_TREE_ZOOM_SCALE_MAX, FAMILY_TREE_ZOOM_SCALE_MIN} from "./ReactFlowZoomControls";
import OtherMembersSectionNode from "./OtherMembersSectionNode";
import {
    clampViewportToCanvas,
    getNodeBounds,
    getPadding,
    GRAPH_TOP_PADDING_PX,
    ViewportLike
} from "./FamilyTreeGraphLayoutUtils";

const nodeTypes = {
    familyMember: FamilyTreeNode,
    otherMembersSection: OtherMembersSectionNode,
};

const edgeTypes = {
    familyTree: FamilyTreeEdge,
};

interface FamilyTreeReactFlowGraphProps {
    nodes: Node[];
    edges: Edge[];
    graphKey: string;
}

const OTHER_MEMBERS_BOTTOM_PADDING_PX = 40;
const FIT_TO_VIEW_MAX_ZOOM = 1;

const FamilyTreeReactFlowGraph: React.FC<FamilyTreeReactFlowGraphProps> = ({nodes, edges, graphKey}) => {
    const {fitView, getViewport, setViewport} = useReactFlow();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({width: 0, height: 0});
    const [renderedEdges, setRenderedEdges] = useState(edges);
    const lastGraphKey = useRef<string | undefined>();
    const initialViewportRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
    const shouldCaptureViewport = useRef(false);
    const pendingFitRef = useRef<number | null>(null);
    const pendingCaptureRef = useRef<number | null>(null);
    const lastFitSignatureRef = useRef<string | null>(null);
    const lastAppliedViewport = useRef<ViewportLike | null>(null);

    const bounds = useMemo(() => getNodeBounds(nodes), [nodes]);

    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const measure = () => {
            const rect = element.getBoundingClientRect();
            const nextWidth = Math.round(rect.width);
            const nextHeight = Math.round(rect.height);

            setContainerSize(prev => {
                if (prev.width === nextWidth && prev.height === nextHeight) return prev;
                return {width: nextWidth, height: nextHeight};
            });
        };

        measure();

        const cleanupFns: Array<() => void> = [];
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure()) : null;
        if (ro) {
            ro.observe(element);
            cleanupFns.push(() => ro.disconnect());
        }

        const handleWindowResize = () => measure();
        window.addEventListener("resize", handleWindowResize);
        cleanupFns.push(() => window.removeEventListener("resize", handleWindowResize));

        return () => {
            cleanupFns.forEach(fn => fn());
        };
    }, []);

    useLayoutEffect(() => {
        setRenderedEdges(edges.length > 0 ? [...edges] : []);
    }, [edges, nodes, graphKey]);

    const captureViewport = useCallback(() => {
        const newViewport = getViewport();
        if (newViewport) {
            initialViewportRef.current = newViewport;
            lastGraphKey.current = graphKey;
        }
    }, [getViewport, graphKey]);

    const applyClamp = useCallback((viewport: ViewportLike) => {
        if (!bounds) return viewport;
        if (containerSize.width === 0 || containerSize.height === 0) return viewport;

        const hasOtherMembersSection = nodes.some(node => node.type === "otherMembersSection");
        const extraBottomPadding = hasOtherMembersSection ? OTHER_MEMBERS_BOTTOM_PADDING_PX : 0;
        return clampViewportToCanvas(
            viewport,
            bounds,
            containerSize.width,
            containerSize.height,
            extraBottomPadding
        );
    }, [bounds, containerSize.width, containerSize.height, nodes]);

    const triggerFitView = useCallback(() => {
        if (!bounds) return;

        if (pendingFitRef.current !== null) {
            cancelAnimationFrame(pendingFitRef.current);
        }
        if (pendingCaptureRef.current !== null) {
            cancelAnimationFrame(pendingCaptureRef.current);
        }

        const padding = getPadding(bounds.width, bounds.height);
        pendingFitRef.current = requestAnimationFrame(() => {
            fitView({padding, maxZoom: FIT_TO_VIEW_MAX_ZOOM});
            shouldCaptureViewport.current = true;
            lastFitSignatureRef.current = bounds.signature;
            pendingFitRef.current = null;
            pendingCaptureRef.current = requestAnimationFrame(() => {
                captureViewport();
                shouldCaptureViewport.current = false;
                pendingCaptureRef.current = null;
            });
        });
    }, [bounds, fitView, captureViewport]);

    const handleMoveEnd = useCallback(() => {
        const newViewport = getViewport();
        if (newViewport) {
            if (shouldCaptureViewport.current) {
                captureViewport();
            }

            const clamped = applyClamp(newViewport);
            const shouldClamp = Math.abs(clamped.x - newViewport.x) > 0.5 || Math.abs(clamped.y - newViewport.y) > 0.5;
            if (shouldClamp) {
                lastAppliedViewport.current = clamped;
                setViewport(clamped);
            }
        }
        shouldCaptureViewport.current = false;
    }, [getViewport, applyClamp, setViewport, captureViewport]);

    const handleMove = useCallback((event: unknown, viewport: ViewportLike) => {
        if (!viewport) return;

        if (lastAppliedViewport.current
            && Math.abs(lastAppliedViewport.current.x - viewport.x) < 0.5
            && Math.abs(lastAppliedViewport.current.y - viewport.y) < 0.5
            && Math.abs(lastAppliedViewport.current.zoom - viewport.zoom) < 0.001
        ) {
            lastAppliedViewport.current = null;
            return;
        }

        const clamped = applyClamp(viewport);
        const shouldClamp = Math.abs(clamped.x - viewport.x) > 0.5 || Math.abs(clamped.y - viewport.y) > 0.5;
        if (shouldClamp) {
            lastAppliedViewport.current = clamped;
            setViewport(clamped);
        }
    }, [applyClamp, setViewport]);

    useLayoutEffect(() => {
        if (!bounds) return;
        const shouldFit = lastGraphKey.current !== graphKey || lastFitSignatureRef.current !== bounds.signature;
        if (shouldFit) {
            triggerFitView();
        }
    }, [bounds, triggerFitView, graphKey]);

    useLayoutEffect(() => {
        return () => {
            if (pendingFitRef.current !== null) {
                cancelAnimationFrame(pendingFitRef.current);
            }
            if (pendingCaptureRef.current !== null) {
                cancelAnimationFrame(pendingCaptureRef.current);
            }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            style={{width: "100%", height: "90%", paddingTop: GRAPH_TOP_PADDING_PX, boxSizing: "border-box"}}
            data-graph-key={graphKey}
        >
            <ReactFlow
                data-testid="family-tree-react-flow"
                style={{position: "relative"}}
                nodes={nodes}
                edges={renderedEdges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodeOrigin={[0.5, 0.5]}
                proOptions={{hideAttribution: true}}
                zoomOnPinch={false}
                zoomOnScroll={false}
                zoomOnDoubleClick={false}
                nodesDraggable={false}
                nodesConnectable={false}
                minZoom={FAMILY_TREE_ZOOM_SCALE_MIN}
                maxZoom={FAMILY_TREE_ZOOM_SCALE_MAX}
                onMove={handleMove}
                onMoveEnd={handleMoveEnd}
            >
                <ReactFlowZoomControls initialViewportRef={initialViewportRef}/>
            </ReactFlow>
        </div>
    );
};

export default FamilyTreeReactFlowGraph;
